import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { useT } from '@/lib/i18n';
import { useOnboardingStore } from '@/lib/store';
import { initSession } from '@/lib/onboarding-api';

export default function WelcomeScreen() {
    const t = useT();
    const navigate = useNavigate();
    const setSession = useOnboardingStore((s) => s.setSession);
    const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('loading');
    const [errMsg, setErrMsg] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        initSession()
            .then((data) => {
                if (!mounted) return;
                setSession(data.sessionId, data.step);
                setStatus('ready');
            })
            .catch((err: Error) => {
                if (!mounted) return;
                setErrMsg(err.message);
                setStatus('error');
            });
        return () => {
            mounted = false;
        };
    }, [setSession]);

    return (
        <MobileFrame showLangToggle>
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '0 24px 32px',
                }}
                className="fade-in"
            >
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 36 }}>
                        <TrustSeal />
                    </div>

                    <h1
                        style={{
                            fontSize: 30,
                            fontWeight: 700,
                            lineHeight: 1.15,
                            whiteSpace: 'pre-line',
                            marginBottom: 14,
                            letterSpacing: '-0.03em',
                        }}
                    >
                        {t.welcomeTitle}
                    </h1>
                    <p
                        style={{
                            fontSize: 15,
                            lineHeight: 1.55,
                            color: 'var(--ink-500)',
                            marginBottom: 32,
                        }}
                    >
                        {t.welcomeSubtitle}
                    </p>

                    <div
                        style={{
                            display: 'flex',
                            gap: 10,
                            marginBottom: 20,
                            padding: '12px 14px',
                            background: 'var(--primary-soft)',
                            borderRadius: 12,
                        }}
                    >
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                background: 'var(--primary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <Icon name="shieldCheck" size={18} color="white" />
                        </div>
                        <div>
                            <div
                                style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: 'var(--primary-dark)',
                                }}
                            >
                                Terdaftar OJK & Kominfo
                            </div>
                            <div
                                style={{ fontSize: 12, color: 'var(--ink-500)', lineHeight: 1.4 }}
                            >
                                Penyelenggara Sertifikasi Elektronik Induk
                            </div>
                        </div>
                    </div>

                    {status === 'error' && (
                        <div className="chip chip-error" style={{ alignSelf: 'flex-start' }}>
                            <Icon name="alert" size={14} /> {errMsg}
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%', marginBottom: 10 }}
                    disabled={status !== 'ready'}
                    onClick={() => navigate('/onboarding/user-type')}
                >
                    {status === 'loading' ? <div className="spinner" /> : t.getStarted}
                    {status === 'ready' && <Icon name="arrowRight" size={18} />}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ width: '100%' }}
                    onClick={() => navigate('/app/login')}
                >
                    {t.haveAccount}
                </button>
            </div>
        </MobileFrame>
    );
}

function TrustSeal() {
    return (
        <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
            <defs>
                <linearGradient id="tsGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="var(--primary)" />
                    <stop offset="100%" stopColor="var(--primary-dark)" />
                </linearGradient>
            </defs>
            <circle
                cx="80"
                cy="80"
                r="76"
                stroke="var(--ink-200)"
                strokeWidth="1"
                strokeDasharray="2 3"
                fill="none"
            />
            <circle
                cx="80"
                cy="80"
                r="66"
                stroke="var(--primary)"
                strokeOpacity="0.15"
                strokeWidth="1"
                fill="none"
            />
            <path
                d="M80 16l56 20v32c0 28-22 52-56 60-34-8-56-32-56-60V36l56-20z"
                fill="url(#tsGrad)"
            />
            <path
                d="M60 82l14 14 28-36"
                stroke="white"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            />
            {Array.from({ length: 24 }).map((_, i) => {
                const a = (i / 24) * Math.PI * 2;
                const x1 = 80 + Math.cos(a) * 72;
                const y1 = 80 + Math.sin(a) * 72;
                const x2 = 80 + Math.cos(a) * 76;
                const y2 = 80 + Math.sin(a) * 76;
                return (
                    <line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke="var(--ink-300)"
                        strokeWidth="1"
                    />
                );
            })}
        </svg>
    );
}
